// @flow

import { useSearchParams } from 'react-router-dom';

import { selectFeatureFlag } from '@gandi/authentication';

import { useAppSelector } from './useAppSelector';

export type FeatureFlag = {
  FF_SHOW_SAVINGS: boolean,
  FF_CART_OVERVIEW_NDS: boolean,
};

/** Check if a feature flag is explicitly enabled or disabled via an URL search param.
 * Do not use for security-critical feature flags.
 */
function useFeatureFlagWithUrl(
  featureFlagName: Parameters<typeof selectFeatureFlag>[1]['featureFlagName'],
): boolean {
  const featureFlagValue = useAppSelector(state =>
    selectFeatureFlag(state, { featureFlagName }),
  );
  const [searchParams] = useSearchParams();

  const hasUrlBypass = searchParams.has(featureFlagName);
  if (!hasUrlBypass) {
    return featureFlagValue;
  }

  const urlFlagValue = searchParams.get(featureFlagName);
  // If the bypass flag is present, any value (even empty, like `?ff-something`), is considered
  // truthy unless explicitely set to false.
  return (
    urlFlagValue !== '0' &&
    urlFlagValue !== 'no' &&
    urlFlagValue !== 'off' &&
    urlFlagValue !== 'false'
  );
}

export const useFeatureFlags = (): FeatureFlag => {
  const FF_SHOW_SAVINGS = useFeatureFlagWithUrl('ff-show-savings');
  const FF_CART_OVERVIEW_NDS = useFeatureFlagWithUrl('ff-cart-overview-nds');

  // And return all feature flags
  return { FF_SHOW_SAVINGS, FF_CART_OVERVIEW_NDS };
};
